/* eslint-disable */
import React from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericCard from "@ui-components/Card/GenericCard";

type CustomModalProps = {
    isOpen: boolean;
    onClose: () => void;
    data: Record<string, any>;
    title?: string;
    children?: React.ReactNode;
    isLoading?: boolean;
};

const formatKey = (key: string) => {
    return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());
};

const formatValue = (value: any): React.ReactNode => {
    if (value === null) {
        return <span className="text-semantic-text-secondary italic text-body">null</span>;
    }

    if (typeof value === "boolean") {
        return (
            <span className={value ? "text-semantic-text-success font-medium text-body" : "text-semantic-text-danger font-medium text-body"}>
                {value ? "Yes ✅" : "No ❌"}
            </span>
        );
    }

    if (typeof value === "string") {
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
        if (isoDateRegex.test(value)) {
            const date = new Date(value);
            return (
                <span className="text-blue-600 text-body text-justify">
                    {date.toLocaleString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                    })}
                </span>
            );
        }
        return <span className="text-body text-right">{value}</span>;
    }

    if (typeof value === "number") {
        return <span className="text-body text-right">{value}</span>;
    }

    if (Array.isArray(value)) {
        return (
            <div className="ml-4 mt-2 flex flex-col gap-2 justify-between">
                {value.map((item, i) => (
                    <GenericCard key={i} className="p-3 bg-gray-50" isCollapsible={false}>
                        {renderObject(item)}
                    </GenericCard>
                ))}
            </div>
        );
    }

    if (typeof value === "object") {
        return <div className="ml-4">{renderObject(value)}</div>;
    }

    return <span className="text-body text-right">{String(value)}</span>;
};

const renderObject = (obj: Record<string, any>, isTopLevel = false): React.ReactNode => {
    const entries = Object.entries(obj);

    // For the top-level, use a two-column grid for the first N fields (e.g., 6 or 8)
    if (isTopLevel) {
        // You can adjust how many fields you want in the first section
        const firstSectionCount = 8;
        const firstSection = entries.slice(0, firstSectionCount);
        const rest = entries.slice(firstSectionCount);

        return (
            <>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-6">
                    {firstSection.map(([key, val]) => (
                        <div className="flex gap-2 justify-between" key={key}>
                            <span className="text-semantic-text-secondary text-body font-bold min-w-[100px]">{formatKey(key)}:</span>
                            {formatValue(val)}
                        </div>
                    ))}
                </div>
                {/* Render the rest as before */}
                <div className="flex flex-col gap-2 justify-between">
                    {rest.map(([key, val]) => (
                        <div key={key}>
                            {typeof val === "object" && val !== null && !Array.isArray(val) ? (
                                <section className="mt-3">
                                    <div className="text-body font-bold mb-1">{formatKey(key)}</div>
                                    <GenericCard className="p-3 bg-gray-50" isCollapsible={false}>
                                        {formatValue(val)}
                                    </GenericCard>
                                </section>
                            ) : (
                                <div className="flex gap-2 justify-between">
                                    <span className="text-semantic-text-secondary text-body font-bold min-w-[100px]">{formatKey(key)}:</span>
                                    {formatValue(val)}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </>
        );
    }

    // Default rendering for nested objects
    return (
        <div className="flex flex-col gap-2 justify-between">
            {entries.map(([key, val]) => (
                <div key={key}>
                    {typeof val === "object" && val !== null && !Array.isArray(val) ? (
                        <section className="mt-3">
                            <div className="text-body font-bold mb-1">{formatKey(key)}</div>
                            <GenericCard className="p-3 bg-gray-50" isCollapsible={false}>
                                {formatValue(val)}
                            </GenericCard>
                        </section>
                    ) : (
                        <div className="flex gap-2 justify-between">
                            <span className="text-semantic-text-secondary text-body font-bold min-w-[100px]">{formatKey(key)}:</span>
                            {formatValue(val)}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

const CustomModal: React.FC<CustomModalProps> = ({ isOpen, onClose, data, title, children, isLoading = false }) => {
    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10">
                    <h2 className="text-xl font-semibold mb-4 text-semantic-text-secondary">
                        Loading data...
                    </h2>
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
                </div>
            );
        }

        if (Object.keys(data).length === 0) {
            return (
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-semantic-text-secondary text-lg">No data available</div>
                </div>
            );
        }

        return renderObject(data, true);
    };

    return (
        <GenericModal
            isOpen={isOpen}
            onClose={onClose}
            title={title || "Details"}
            size="lg"
        >
            <div className="pt-2 pr-4 min-h-[400px] max-h-[calc(100vh-200px)] overflow-y-auto relative">
                {children}
                {renderContent()}
            </div>
        </GenericModal>
    );
};

export default CustomModal; 